import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Trash2, Edit2, Link2, Unlink, ChevronDown, ChevronUp, Lock, Search, Filter, X, Save, Layers, Download, Upload } from 'lucide-react';

const FilterSetManager = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();

  const [filterSets, setFilterSets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedSet, setExpandedSet] = useState(null);

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingSet, setEditingSet] = useState(null);
  const [setName, setSetName] = useState('');
  const [setDesc, setSetDesc] = useState('');
  const [attributes, setAttributes] = useState([]);
  const [saving, setSaving] = useState(false);

  // Map modal
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapSetId, setMapSetId] = useState('');
  const [mapCatId, setMapCatId] = useState('');
  const [mapSubId, setMapSubId] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [sets, cats, maps] = await Promise.all([
        apiCall('/api/filter-sets/'),
        apiCall('/api/categories/?is_active=true'),
        apiCall('/api/filter-sets/mappings/all')
      ]);
      setFilterSets(Array.isArray(sets) ? sets : []);
      setCategories(Array.isArray(cats) ? cats : []);
      setMappings(Array.isArray(maps) ? maps : []);
    } catch (e) {
      console.error('Failed to load filter set data:', e);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---- Attribute Helpers ----
  const addAttribute = () => {
    setAttributes(prev => [...prev, {
      key: 'custom.',
      label: '',
      input_type: 'select',
      allowed_values: [],
      required: false,
      sort_order: prev.length,
      _newValue: '' // temp field for adding values one by one
    }]);
  };

  const updateAttr = (idx, field, value) => {
    setAttributes(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const removeAttr = (idx) => {
    setAttributes(prev => prev.filter((_, i) => i !== idx));
  };

  const addValueToAttr = (idx) => {
    const attr = attributes[idx];
    const val = (attr._newValue || '').trim();
    if (!val) return;
    if (attr.allowed_values.includes(val)) {
      toast({ title: 'Duplicate', description: `"${val}" already exists`, variant: 'destructive' });
      return;
    }
    setAttributes(prev => prev.map((a, i) =>
      i === idx ? { ...a, allowed_values: [...a.allowed_values, val], _newValue: '' } : a
    ));
  };

  const removeValueFromAttr = (attrIdx, valIdx) => {
    setAttributes(prev => prev.map((a, i) =>
      i === attrIdx ? { ...a, allowed_values: a.allowed_values.filter((_, vi) => vi !== valIdx) } : a
    ));
  };

  // ---- CRUD ----
  const openNew = () => {
    setEditingSet(null);
    setSetName('');
    setSetDesc('');
    setAttributes([]);
    setShowModal(true);
  };

  const openEdit = (set) => {
    setEditingSet(set);
    setSetName(set.name);
    setSetDesc(set.description || '');
    setAttributes((set.attributes || []).map(a => ({ ...a, _newValue: '' })));
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!setName.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    const cleanAttrs = attributes
      .filter(a => a.key && a.label)
      .map(({ _newValue, ...rest }, i) => ({ ...rest, sort_order: i }));

    setSaving(true);
    try {
      const payload = { name: setName.trim(), description: setDesc, attributes: cleanAttrs };
      if (editingSet) {
        await apiCall(`/api/filter-sets/set/${editingSet.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast({ title: 'Filter set updated' });
      } else {
        await apiCall('/api/filter-sets/', { method: 'POST', body: JSON.stringify(payload) });
        toast({ title: 'Filter set created' });
      }
      setShowModal(false);
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this filter set and all its category mappings?')) return;
    try {
      await apiCall(`/api/filter-sets/set/${id}`, { method: 'DELETE' });
      toast({ title: 'Deleted' });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleCreateMapping = async () => {
    if (!mapSetId || !mapCatId) return;
    try {
      await apiCall('/api/filter-sets/mappings', {
        method: 'POST',
        body: JSON.stringify({
          filter_set_id: mapSetId,
          category_id: mapCatId,
          subcategory_id: mapSubId || null
        })
      });
      toast({ title: 'Mapping created' });
      setShowMapModal(false);
      setMapSetId(''); setMapCatId(''); setMapSubId('');
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteMapping = async (id) => {
    try {
      await apiCall(`/api/filter-sets/mappings/${id}`, { method: 'DELETE' });
      toast({ title: 'Mapping removed' });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // ---- Export / Import ----
  const handleExport = async () => {
    try {
      const data = await apiCall('/api/filter-sets/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `filter-sets-export-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Exported', description: `${data.counts?.filter_sets || 0} sets and ${data.counts?.mappings || 0} mappings exported.` });
    } catch (e) {
      toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.filter_sets?.length) {
        toast({ title: 'Invalid file', description: 'No filter sets found in the JSON file.', variant: 'destructive' });
        return;
      }
      const result = await apiCall('/api/filter-sets/import', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      toast({ title: 'Imported', description: result.message });
      fetchData();
    } catch (err) {
      toast({ title: 'Import failed', description: err.message, variant: 'destructive' });
    } finally {
      e.target.value = '';
    }
  };


  // ---- Lookup Helpers ----
  const getCatName = (id) => {
    const cat = categories.find(c => c.id === id);
    if (!cat) return id;
    const types = cat.listing_types?.length ? ` (${cat.listing_types.join(', ')})` : '';
    return `${cat.name}${types}`;
  };
  const getSubName = (catId, subId) => {
    const cat = categories.find(c => c.id === catId);
    return cat?.subcategories?.find(s => s.id === subId)?.name || subId;
  };
  const setMappingsFor = (setId) => mappings.filter(m => m.filter_set_id === setId);
  const selectedCatSubs = (categories.find(c => c.id === mapCatId)?.subcategories || []).filter(s => s.is_active !== false);

  const filtered = search
    ? filterSets.filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    : filterSets;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="filter-set-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Layers size={24} className="text-violet-600" />
              Filter Sets
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage Shopify Metafield-linked attributes for storefront search filters.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-sets-btn">
              <Download size={15} className="mr-1.5" /> Export
            </Button>
            <label>
              <Button variant="outline" size="sm" asChild data-testid="import-sets-btn">
                <span><Upload size={15} className="mr-1.5" /> Import</span>
              </Button>
              <input type="file" accept=".json" className="hidden" onChange={handleImport} />
            </label>
            <Button variant="outline" size="sm" onClick={() => setShowMapModal(true)} data-testid="add-mapping-btn">
              <Link2 size={15} className="mr-1.5" /> Link to Category
            </Button>
            <Button size="sm" onClick={openNew} data-testid="create-set-btn">
              <Plus size={15} className="mr-1.5" /> New Filter Set
            </Button>
          </div>
        </div>

        {/* Search */}
        {filterSets.length > 3 && (
          <div className="relative max-w-xs">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" />
          </div>
        )}

        {/* Filter Sets */}
        {filtered.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <Filter size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium text-gray-600">No filter sets yet</p>
              <p className="text-sm text-gray-400 mt-1 mb-4">Create your first filter set to define product attributes for Shopify filters.</p>
              <Button onClick={openNew}><Plus size={15} className="mr-1.5" /> Create First Filter Set</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(set => {
              const sm = setMappingsFor(set.id);
              const open = expandedSet === set.id;
              return (
                <Card key={set.id} className={`transition-all ${open ? 'border-violet-300 shadow-sm' : 'border-gray-200'}`} data-testid={`filter-set-${set.id}`}>
                  <div
                    className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50/50"
                    onClick={() => setExpandedSet(open ? null : set.id)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center text-sm font-bold shrink-0">
                        {set.attributes?.length || 0}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{set.name}</p>
                        {set.description && <p className="text-xs text-gray-400 truncate">{set.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {sm.length > 0 && <Badge variant="secondary" className="text-[10px]">{sm.length} linked</Badge>}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openEdit(set); }}>
                        <Edit2 size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={(e) => { e.stopPropagation(); handleDelete(set.id); }}>
                        <Trash2 size={14} />
                      </Button>
                      {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    </div>
                  </div>

                  {open && (
                    <CardContent className="pt-0 pb-4 border-t border-gray-100">
                      {/* Attributes */}
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Attributes</p>
                        <div className="space-y-2">
                          {set.attributes?.map((attr) => (
                            <div key={attr.key} className="bg-gray-50 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm text-gray-800">{attr.label}</span>
                                <code className="text-[10px] bg-gray-200 px-1.5 py-0.5 rounded font-mono text-gray-600">{attr.key}</code>
                                <Badge variant="outline" className="text-[10px] capitalize">{attr.input_type?.replace('_', ' ')}</Badge>
                                {attr.required && <Badge className="text-[10px] bg-orange-100 text-orange-700 border-orange-200">Required</Badge>}
                              </div>
                              {attr.allowed_values?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {attr.allowed_values.map(v => (
                                    <span key={v} className="text-[11px] bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-600">{v}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Category Links */}
                      <div className="mt-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Linked Categories</p>
                        {sm.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">Not linked to any category.
                            <button className="text-violet-600 ml-1 hover:underline" onClick={() => { setMapSetId(set.id); setShowMapModal(true); }}>Link now</button>
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {sm.map(m => (
                              <div key={m.id} className="flex items-center gap-1.5 bg-blue-50 rounded-full px-3 py-1 text-xs text-blue-800">
                                <span>{getCatName(m.category_id)}{m.subcategory_id ? ` / ${getSubName(m.category_id, m.subcategory_id)}` : ' (all sub-cats)'}</span>
                                <button onClick={() => handleDeleteMapping(m.id)} className="text-blue-400 hover:text-red-500">
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* ==================== CREATE / EDIT MODAL ==================== */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSet ? 'Edit Filter Set' : 'New Filter Set'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-5">
              {/* Name & Description */}
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Filter Set Name</Label>
                  <Input value={setName} onChange={(e) => setSetName(e.target.value)} placeholder="e.g. Furniture Specs, Food Attributes" className="mt-1" data-testid="set-name-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Description <span className="text-gray-400 font-normal">(optional)</span></Label>
                  <Input value={setDesc} onChange={(e) => setSetDesc(e.target.value)} placeholder="What products is this for?" className="mt-1" />
                </div>
              </div>

              {/* Attributes */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-semibold">Attributes</Label>
                  <Button variant="outline" size="sm" onClick={addAttribute} className="h-7 text-xs" data-testid="add-attr-btn">
                    <Plus size={13} className="mr-1" /> Add Attribute
                  </Button>
                </div>

                {attributes.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4 border border-dashed rounded-lg">
                    No attributes yet. Click "Add Attribute" to start.
                  </p>
                )}

                <div className="space-y-4">
                  {attributes.map((attr, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50 space-y-3" data-testid={`attr-block-${idx}`}>
                      {/* Row 1: Key + Label */}
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-gray-500">Shopify Metafield Key</Label>
                          <Input
                            value={attr.key}
                            onChange={(e) => updateAttr(idx, 'key', e.target.value)}
                            placeholder="custom.attribute_name"
                            className="h-8 text-sm font-mono"
                            data-testid={`attr-key-${idx}`}
                          />
                        </div>
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-gray-500">Display Label</Label>
                          <Input
                            value={attr.label}
                            onChange={(e) => updateAttr(idx, 'label', e.target.value)}
                            placeholder="e.g. Material, Bed Size"
                            className="h-8 text-sm"
                            data-testid={`attr-label-${idx}`}
                          />
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8 mt-5 text-red-400 hover:text-red-600 shrink-0" onClick={() => removeAttr(idx)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>

                      {/* Row 2: Type + Required */}
                      <div className="flex items-center gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Input Type</Label>
                          <Select value={attr.input_type} onValueChange={(v) => updateAttr(idx, 'input_type', v)}>
                            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="select">Dropdown</SelectItem>
                              <SelectItem value="multi_select">Multi-select</SelectItem>
                              <SelectItem value="text">Text Input</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <label className="flex items-center gap-1.5 text-xs mt-5 cursor-pointer select-none">
                          <input type="checkbox" checked={attr.required} onChange={(e) => updateAttr(idx, 'required', e.target.checked)} className="rounded border-gray-300" />
                          <Lock size={11} className="text-orange-500" /> Required for vendors
                        </label>
                      </div>

                      {/* Row 3: Allowed Values (one by one) */}
                      {attr.input_type !== 'text' && (
                        <div className="space-y-2">
                          <Label className="text-xs text-gray-500">Allowed Values <span className="text-gray-400">(must match Shopify Metafield definitions)</span></Label>

                          {/* Existing values as removable chips */}
                          {attr.allowed_values.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {attr.allowed_values.map((v, vi) => (
                                <span key={vi} className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2.5 py-0.5 text-xs text-gray-700">
                                  {v}
                                  <button onClick={() => removeValueFromAttr(idx, vi)} className="text-gray-400 hover:text-red-500">
                                    <X size={11} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Add new value input */}
                          <div className="flex gap-1.5">
                            <Input
                              value={attr._newValue || ''}
                              onChange={(e) => updateAttr(idx, '_newValue', e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addValueToAttr(idx); } }}
                              placeholder="Type a value and press Enter"
                              className="h-8 text-sm flex-1"
                              data-testid={`attr-add-value-${idx}`}
                            />
                            <Button variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={() => addValueToAttr(idx)}>
                              Add
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-set-btn">
                <Save size={15} className="mr-1.5" />
                {saving ? 'Saving...' : editingSet ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ==================== LINK TO CATEGORY MODAL ==================== */}
        <Dialog open={showMapModal} onOpenChange={setShowMapModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Link Filter Set to Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Filter Set</Label>
                <Select value={mapSetId} onValueChange={setMapSetId}>
                  <SelectTrigger className="mt-1" data-testid="map-set-select"><SelectValue placeholder="Select a filter set" /></SelectTrigger>
                  <SelectContent>
                    {filterSets.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name} ({s.attributes?.length} attrs)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={mapCatId} onValueChange={(v) => { setMapCatId(v); setMapSubId(''); }}>
                  <SelectTrigger className="mt-1" data-testid="map-cat-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => {
                      const types = c.listing_types?.length ? ` (${c.listing_types.join(', ')})` : '';
                      return (
                        <SelectItem key={c.id} value={c.id}>{c.name}{types}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sub-category <span className="text-xs text-gray-400 font-normal">(leave blank to apply to all)</span></Label>
                <Select value={mapSubId || '__all__'} onValueChange={(v) => setMapSubId(v === '__all__' ? '' : v)}>
                  <SelectTrigger className="mt-1" data-testid="map-sub-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All sub-categories (inherited)</SelectItem>
                    {selectedCatSubs.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMapModal(false)}>Cancel</Button>
              <Button onClick={handleCreateMapping} disabled={!mapSetId || !mapCatId} data-testid="save-mapping-btn">
                <Link2 size={15} className="mr-1.5" /> Link
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default FilterSetManager;
